import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Cluster, FilterBar, Grid, HeaderActions, Inline, LoadMorePager, SegmentedControl, Select, Stack, StatusChip } from '../components/ui';
import { EntityListPage, type EntityColumn } from '../components/templates/EntityListPage';
import { NewSubjectDialog } from '../components/sites/NewSubjectDialog';
import { domainIcon } from '../components/sites/domainIcon';
import { PropertyMap, type MappableRecord } from '../components/properties/PropertyMap';
import { listSubjects, subjectsKeys, type Subject } from '../lib/api/subjects.gen';
import { useCursorList } from '../lib/query/useCursorList';
import { errorMessage } from '../lib/errorMessage';
import { formatDistanceToNow } from '../lib/time';
import { useAuth } from '../lib/auth';
import { useCapabilities } from '../lib/capabilities';
import { useVocab, SITES_REGISTRY_LABEL, SITES_REGISTRY_LABEL_SINGULAR } from '../lib/vocab';

// /app/sites — the canonical Sites registry (P03 §0, §5, §7.1; story 3-1b).
//
// NAMING, which P03 §0 makes the whole point: the REGISTRY is "Sites" and that
// string is a CONSTANT (nav row, breadcrumb, this heading — `SITES_REGISTRY_LABEL`,
// never `vocab()`); every RECORD noun resolves per pack through
// `vocab('Subject', row.domainKey)`, so one table can read "Building" on one
// row and "Website" on the next.

type ViewMode = 'list' | 'grid' | 'map';
type StatusFilter = 'active' | 'archived' | 'all';

const PAGE_SIZE = 25;
const VIEW_LABEL: Record<ViewMode, string> = { list: 'List', grid: 'Grid', map: 'Map' };
const STATUSES: StatusFilter[] = ['active', 'archived', 'all'];
const isStatus = (value: string | null): value is StatusFilter =>
  value !== null && (STATUSES as string[]).includes(value);

export function SitesPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = useCapabilities();
  const { vocab, activePacks, packs, defaultDomainKey } = useVocab();

  // ── URL STATE (G-25) ─────────────────────────────────────────────────────
  // Filters live in the query string, not in component state: an encoded URL
  // renders that exact state on a cold load, Back/Forward move between filter
  // states, and a link is shareable. `replace: false` on a filter change is
  // deliberate — a filter IS a navigation step here.
  const q = params.get('q') ?? '';
  const domain = params.get('domain') ?? '';
  const status: StatusFilter = isStatus(params.get('status')) ? (params.get('status') as StatusFilter) : 'active';
  const rawView = params.get('view');
  const view: ViewMode = rawView === 'grid' || rawView === 'map' ? rawView : 'list';

  const setParam = useCallback(
    (key: string, value: string, defaultValue = '') => {
      const next = new URLSearchParams(params);
      if (!value || value === defaultValue) next.delete(key);
      else next.set(key, value);
      setParams(next);
    },
    [params, setParams],
  );

  const filters = useMemo(
    () => ({
      ...(q ? { q } : {}),
      ...(domain ? { domain } : {}),
      status,
    }),
    [q, domain, status],
  );

  // ── The list (G-18) ──────────────────────────────────────────────────────
  // Every filter is a SERVER parameter, so `total` counts the filtered
  // collection and page 2 continues the filtered order. Filtering a loaded
  // page in the browser would make both of those lies. The filters are part of
  // the query key, so changing one starts a new cursor walk rather than
  // appending to the old one.
  const {
    items: subjects,
    total,
    hasMore,
    fetchNextPage,
    isLoading,
    isLoadingMore,
    isError,
    error,
    refetch,
  } = useCursorList<Subject>({
    queryKey: subjectsKeys.list({ org: user?.organizationId, ...filters }),
    fetchPage: (page) => listSubjects({ ...page, ...filters }),
    limit: PAGE_SIZE,
    enabled: Boolean(user),
  });

  const canManage = hasPermission('subjects:manage');
  const registryNoun = SITES_REGISTRY_LABEL.toLowerCase();

  // WHICH PACKS MAY A NEW RECORD USE (P03 §3.1 + story 2-20's rule).
  // Normally: the packs the org has activated. But the DEFAULT pack is the base
  // product, not an entitlement — 2-20 removed `domains.building` from the Sites
  // nav row precisely because a free org was being teased with an upsell for a
  // surface it already had, and the dev `freeOwner` persona really does resolve
  // ZERO active packs. Falling back to the default pack keeps that org able to
  // create records, and matches what the endpoint accepts (see
  // `resolvePackForWrite` in backend/src/services/subject.service.js) — one rule,
  // asserted on both sides, rather than a form that offers what the API refuses.
  const selectablePacks = useMemo(() => {
    if (activePacks.length > 0) return activePacks;
    const fallback = packs.find((pack) => pack.key === defaultDomainKey);
    return fallback ? [fallback] : [];
  }, [activePacks, packs, defaultDomainKey]);

  // The unqualified noun labels the CREATE affordance (no record exists yet to
  // read a domainKey from); every row below qualifies by its own pack. With more
  // than one pack selectable the CTA must stay domain-NEUTRAL — the dialog is
  // where the domain gets chosen — so it falls back to the registry's own
  // singular, which P03 §0 makes a constant.
  const newNoun =
    selectablePacks.length === 1 && selectablePacks[0]
      ? vocab('Subject', selectablePacks[0].key).toLowerCase()
      : SITES_REGISTRY_LABEL_SINGULAR.toLowerCase();

  const filtersApplied = Boolean(q || domain || status !== 'active');

  const listActions = (
    // Story 2-147wb: header-action consistency pass across the four
    // collection pages (Sites/Inspections/Templates/Reports) — this was a
    // raw `Button` with the `+` icon inserted as a hand-written JSX child
    // instead of `Button`'s own `icon` prop, the one page of the four whose
    // primary create action did NOT go through `HeaderActions` (story
    // 2-147r). `Cluster` (2-147sa) stays the SegmentedControl's own
    // wrapper — same "host owns the Button cluster, Cluster owns everything
    // else in the row" split this lane already uses on `ItemsListPage`.
    <Cluster gap={2}>
      {/* Story 2-147h: composes the same `SegmentedControl` primitive
          ItemsListPage already uses for its List/Cards toggle (2-139) —
          this was three hand-rolled `Button`s with no radiogroup semantics
          until this story converged it. List (table rows, operational
          scanning) / Grid (visual card browse, the same touch-optimized
          reading 2-139 gives Cards) / Map (geospatial "where is it") are
          three genuinely different renderings for three genuinely
          different tasks — earned per 2-139's own test, not the dead
          affordance class 2-107 removed. */}
      <SegmentedControl<ViewMode>
        aria-label="View"
        value={view}
        onChange={(next) => setParam('view', next, 'list')}
        options={(['list', 'grid', 'map'] as ViewMode[]).map((mode) => ({ value: mode, label: VIEW_LABEL[mode] }))}
      />
      {/* G-13: the CTA renders only for a principal who actually holds the
          permission the endpoint enforces — never a control that leads to a 403.
          `icon` matches Templates' "Create template" convention — every
          primary "create a new record" header action across these four
          pages now carries the same leading `+`. */}
      <HeaderActions
        actions={
          canManage
            ? [
                {
                  id: 'add',
                  label: `Add ${newNoun}`,
                  variant: 'primary',
                  icon: <PlusIcon className="h-4 w-4" aria-hidden="true" />,
                  onClick: () => setShowForm(true),
                },
              ]
            : []
        }
      />
    </Cluster>
  );

  const rowNoun = (subject: Subject): string => vocab('Subject', subject.domainKey);

  // Map pins come from whatever the record's pack calls its address field —
  // today that is the building pack's `address`, shaped exactly like the legacy
  // Property address (3-1a copied it verbatim, which is why this needs no
  // translation layer).
  const mappable: MappableRecord[] = useMemo(
    () =>
      subjects
        .map((subject) => {
          const address = (subject.attributes as Record<string, unknown> | undefined)?.address;
          if (!address || typeof address !== 'object') return null;
          return { _id: subject._id, name: subject.name, address } as MappableRecord;
        })
        .filter((record): record is MappableRecord => record !== null),
    [subjects],
  );

  const columns: EntityColumn<Subject>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (subject) => {
        const pack = packs.find((p) => p.key === subject.domainKey);
        const Icon = domainIcon(pack?.icon);
        return (
          // Story 2-147wb: was a raw `flex items-center gap-3` — `Inline`
          // (2-147sa) is the correct primitive here (an icon beside its own
          // label, never wrapping mid-pair), not `Cluster`.
          <Inline gap={3}>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
              <Icon className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block truncate font-medium text-foreground">{subject.name}</span>
              <span className="block truncate text-xs text-muted-foreground">{rowNoun(subject)}</span>
            </span>
          </Inline>
        );
      },
    },
    {
      key: 'reference',
      header: 'Reference',
      render: (subject) => <span className="text-muted-foreground">{subject.referenceCode || '—'}</span>,
    },
    {
      key: 'lastInspection',
      header: 'Last inspection',
      render: (subject) => (
        <span className="text-muted-foreground">
          {subject.lastInspectionAt ? formatDistanceToNow(subject.lastInspectionAt) : 'Never'}
        </span>
      ),
    },
    {
      key: 'nextScheduled',
      header: 'Next scheduled',
      render: (subject) => (
        <span className="text-muted-foreground">
          {subject.nextScheduledAt ? new Date(subject.nextScheduledAt).toLocaleDateString() : 'Not scheduled'}
        </span>
      ),
    },
    {
      key: 'state',
      header: 'State',
      // Story 2-146b: same conversion as `SiteDetailPage.tsx` (this row's own
      // detail view) — one archived/active vocabulary, one primitive.
      render: (subject) =>
        subject.archivedAt ? (
          <StatusChip tone="neutral" weight="bold" label="Archived" value="archived" />
        ) : (
          <StatusChip tone="success" label="Active" value="active" />
        ),
    },
  ];

  const filterBar = (
    <FilterBar
      loading={isLoading}
      clearable={filtersApplied}
      onClear={() => setParams(new URLSearchParams(view === 'grid' ? { view: 'grid' } : {}))}
      keyword={{
        value: q,
        onChange: (event) => setParam('q', event.target.value),
        onClear: () => setParam('q', ''),
        placeholder: `Search ${registryNoun}…`,
        'aria-label': `Search ${registryNoun}`,
      }}
    >
      <Select
        size="sm"
        value={domain}
        aria-label="Domain"
        data-testid="filter-domain"
        onChange={(event) => setParam('domain', event.target.value)}
      >
        <option value="">All domains</option>
        {packs.map((pack) => (
          <option key={pack.key} value={pack.key}>
            {pack.subjectSchema.labels.plural}
          </option>
        ))}
      </Select>
      <Select
        size="sm"
        value={status}
        aria-label="State"
        data-testid="filter-status"
        onChange={(event) => setParam('status', event.target.value, 'active')}
      >
        <option value="active">Active</option>
        <option value="archived">Archived</option>
        <option value="all">All states</option>
      </Select>
    </FilterBar>
  );

  return (
    <>
      {showForm && (
        <NewSubjectDialog
          activePacks={selectablePacks}
          onClose={() => setShowForm(false)}
          onCreated={(subject) => {
            setShowForm(false);
            navigate(`/app/sites/${subject._id}`);
          }}
        />
      )}

      <EntityListPage<Subject>
        title={SITES_REGISTRY_LABEL}
        description="Everything your team inspects, whatever field of inspection it belongs to."
        actions={listActions}
        filters={filterBar}
        isLoading={isLoading}
        empty={
          isError
            ? {
                title: 'That list could not be loaded',
                // G-27: the server's own sentence — a plan/permission gate here
                // (e.g. a feature not on this org's plan) previously showed the
                // same hardcoded copy as a network hiccup, hiding the real reason.
                description: errorMessage(error),
                actionLabel: 'Retry',
                onAction: refetch,
              }
            : filtersApplied
              ? {
                  title: 'Nothing matches those filters',
                  description: 'Clear the filters to see every record, or adjust the search.',
                  actionLabel: 'Clear filters',
                  onAction: () => setParams(new URLSearchParams()),
                }
              : {
                  title: `No records yet`,
                  description: `Add your first ${newNoun} to start creating inspections and AI-powered reports.`,
                  ...(canManage ? { actionLabel: `Add your first ${newNoun}`, onAction: () => setShowForm(true) } : {}),
                }
        }
        footer={
          // Story 2-147tb: converged onto the canonical `LoadMorePager`
          // (P10 §2.7 row 7) — was a hand-rolled copy of the same footer
          // markup six other collection pages also carried independently.
          <LoadMorePager
            shownCount={subjects.length}
            total={total}
            hasMore={hasMore}
            onLoadMore={fetchNextPage}
            isLoadingMore={isLoadingMore}
          />
        }
        {...(view === 'list'
          ? {
              columns,
              rows: subjects,
              rowKey: (subject: Subject) => subject._id,
              onRowClick: (subject: Subject) => navigate(`/app/sites/${subject._id}`),
            }
          : {})}
      >
        {view === 'map' && subjects.length > 0 ? (
          <div className="rounded-hairline border border-border bg-card relative h-[600px] overflow-hidden">
            {/* Only address-bearing records can be pinned; a pack with no
                address contributes nothing rather than a fabricated marker. */}
            <PropertyMap
              properties={mappable}
              onPropertyClick={(record) => navigate(`/app/sites/${record._id}`)}
            />
            {mappable.length < subjects.length && (
              <p className="absolute bottom-3 left-3 rounded-lg bg-background/90 px-3 py-1.5 text-xs text-muted-foreground">
                {subjects.length - mappable.length} of {subjects.length} records have no address and are not
                shown on the map.
              </p>
            )}
          </div>
        ) : view === 'grid' && subjects.length > 0 ? (
          <Grid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} gap={4}>
            {subjects.map((subject) => {
              const pack = packs.find((p) => p.key === subject.domainKey);
              const Icon = domainIcon(pack?.icon);
              return (
                // `Link` cannot render `as` one of `Stack`'s intrinsic tags
                // (it's a router component, not a string tag) — the card's
                // own border/padding/hover chrome AND its `flex flex-col`
                // (no gap utility of its own, so it does not itself re-trip
                // `raw-flex`) stay on `Link`, kept as a flex column ONLY so
                // the trailing "Last inspected" line's `mt-auto` still pins
                // it to the card's bottom edge regardless of content height
                // above it — `Stack` (2-147sa) composes the vertical rhythm
                // of the first three items, nested inside (story 2-147wb;
                // was a raw `flex flex-col gap-2` on this same element).
                // Live-verified fix: the original relied on `Link`'s own
                // `flex flex-col` "blockifying" every direct child `<span>`
                // onto its own line (a real CSS flexbox behavior — flex
                // items are blockified regardless of their own `display`).
                // Nested one level inside `Stack` (a plain `space-y-*` div,
                // not a flex container), that blockification no longer
                // reaches these grandchild spans — caught live in the
                // running app (name and reference ran together on one line,
                // e.g. "E2E Canonical Site CE2E-SITE-C"), not assumed from
                // eslint/tsc passing. `block` (same explicit-block pattern
                // this file's own list-view `name` column render already
                // uses one column over) fixes it for real.
                <Link
                  key={subject._id}
                  to={`/app/sites/${subject._id}`}
                  className="rounded-hairline border border-border bg-card focus-ring flex flex-col p-4 text-left transition-all hover:shadow-lg"
                >
                  <Stack gap={2}>
                    <Inline gap={2} className="text-xs text-muted-foreground">
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      {rowNoun(subject)}
                    </Inline>
                    <span className="block truncate font-semibold text-foreground">{subject.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {subject.referenceCode || '—'}
                    </span>
                  </Stack>
                  <span className="mt-auto pt-2 text-xs text-muted-foreground">
                    {subject.lastInspectionAt
                      ? `Last inspected ${formatDistanceToNow(subject.lastInspectionAt)}`
                      : 'Never inspected'}
                  </span>
                </Link>
              );
            })}
          </Grid>
        ) : null}
      </EntityListPage>
    </>
  );
}
